// What floats over a screen: a sheet, a hint, a popover, a menu. The kit's
// half of LOOKS.md Part 7 — a clean screen asks for one of these by what it is
// for, and the look (through the shared tokens) and the component library
// underneath are the kit's business, not the screen's.

import {
  cloneElement,
  isValidElement,
  useState,
  type MouseEvent as PointerClick,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  Drawer,
  Menu as MantineMenu,
  Modal,
  Popover as MantinePopover,
  Tooltip,
} from '@mantine/core';
import { isNarrowWidth, isPhoneWidth, useViewportWidth } from './viewport';
import { Icon, type IconName } from './Icon';
import { IconButton } from './ui';
import { BackCloses } from './backCloses';
import { PullDown } from './sheetDrag';
import { HELP_DELAY_MS } from './Why';
import { WindowTier } from '../shell/useTier';

/**
 * A sheet that slides in: from the right on tablet and desktop, from the
 * bottom on a phone (TEMPLATES.md, DetailSheet). `wide` is for two rosters
 * side by side; `zIndex` puts it above a full-screen moment; `closeOnEscape`
 * is false while something inside it owns Escape (a color picker's dropdown).
 * `peek` is for a sheet whose point is the screen behind it (Appearance,
 * B-399): a light veil with no blur, and on a phone a little over half the
 * height, so a change made in it is seen on the screen it was made for.
 */
export function Sheet({
  opened,
  onClose,
  title,
  children,
  wide = false,
  zIndex,
  closeOnEscape = true,
  from,
  height,
  peek = false,
}: {
  opened: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  wide?: boolean;
  zIndex?: number;
  closeOnEscape?: boolean;
  /** Always from the bottom, at a little over half the height: the tablet's handle. */
  from?: 'bottom';
  /** From the bottom at this many pixels: a menu sized to its entries. */
  height?: number;
  peek?: boolean;
}) {
  const phone = useViewportWidth(isPhoneWidth);
  const bottom = phone || from === 'bottom' || height !== undefined;
  // Beside the drawer, not in it: the drawer unmounts what it holds when it
  // shuts, and this has to hear it shut.
  return (
    <>
      {/* A phone's Back closes it before it leaves the screen (UX-13). */}
      <BackCloses opened={opened} onClose={onClose} />
      <Drawer
        opened={opened}
        onClose={onClose}
        position={bottom ? 'bottom' : 'right'}
        size={
          height !== undefined
            ? height
            : from === 'bottom'
              ? '55%'
              : phone
                ? peek
                  ? '62%'
                  : '90%'
                : wide
                  ? 720
                  : 440
        }
        title={title}
        radius={bottom ? 'lg' : 0}
        overlayProps={
          peek ? { backgroundOpacity: 0.12, blur: 0 } : { backgroundOpacity: 0.5, blur: 4 }
        }
        transitionProps={{ duration: 220, timingFunction: 'cubic-bezier(.2,.8,.2,1)' }}
        closeButtonProps={{ 'aria-label': 'Close' }}
        zIndex={zIndex}
        closeOnEscape={closeOnEscape}
        // From the bottom it has a grabber and pulls down to close (UX-40).
        classNames={{ content: bottom ? 'fsp-ui-sheet fsp-ui-sheet-bottom' : 'fsp-ui-sheet' }}
      >
        {bottom ? <PullDown onClose={onClose} /> : null}
        {/* The window's size, so the window's tier, even opened from a pane. */}
        <WindowTier>{children}</WindowTier>
      </Drawer>
    </>
  );
}

/**
 * A whole window for making something, over whatever opened it: the face
 * maker (B-488). Full screen on a phone and a tablet, where a sheet would
 * leave the thing being made too small to work on; a large window on a
 * desktop, with the screen behind it dimmed. Its body is a column that fills
 * the window, so what it holds decides which part scrolls.
 */
export function Workshop({
  opened,
  onClose,
  title,
  children,
  closeOnEscape = true,
}: {
  opened: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  closeOnEscape?: boolean;
}) {
  const full = useViewportWidth(isNarrowWidth);
  return (
    <>
      <BackCloses opened={opened} onClose={onClose} />
      <Modal
        opened={opened}
        onClose={onClose}
        title={title}
        fullScreen={full}
        centered
        size={1040}
        radius={full ? 0 : 'lg'}
        closeOnEscape={closeOnEscape}
        closeButtonProps={{ 'aria-label': 'Close' }}
        overlayProps={{ backgroundOpacity: 0.55, blur: 4 }}
        transitionProps={{ transition: full ? 'slide-up' : 'pop', duration: 200 }}
        classNames={{
          content: full ? 'fsp-ui-workshop fsp-ui-workshop-full' : 'fsp-ui-workshop',
          body: 'fsp-ui-workshop-body',
        }}
      >
        <WindowTier>{children}</WindowTier>
      </Modal>
    </>
  );
}

/**
 * A panel at the side that the screen behind stays readable under — chat, the
 * notification center (CHAT.md: it never navigates away). From the right on
 * tablet and desktop, a tall sheet from the bottom on a phone; its body has no
 * padding and fills the height, since what it holds scrolls itself.
 */
export function SidePanel({
  opened,
  onClose,
  title,
  closeLabel,
  children,
}: {
  opened: boolean;
  onClose: () => void;
  title: ReactNode;
  closeLabel: string;
  children: ReactNode;
}) {
  const phone = useViewportWidth(isPhoneWidth);
  return (
    <>
      <BackCloses opened={opened} onClose={onClose} />
      <Drawer
        opened={opened}
        onClose={onClose}
        position={phone ? 'bottom' : 'right'}
        size={phone ? '92%' : 390}
        title={title}
        radius={phone ? 'lg' : 0}
        overlayProps={{ backgroundOpacity: phone ? 0.4 : 0.12, blur: 0 }}
        transitionProps={{ duration: 220, timingFunction: 'cubic-bezier(.2,.8,.2,1)' }}
        closeButtonProps={{ 'aria-label': closeLabel }}
        classNames={{
          content: phone ? 'fsp-ui-sheet fsp-ui-sheet-bottom' : 'fsp-ui-sheet',
          body: 'fsp-ui-side-panel-body',
        }}
      >
        {phone ? <PullDown onClose={onClose} /> : null}
        <WindowTier>{children}</WindowTier>
      </Drawer>
    </>
  );
}

/**
 * A few words that appear on hover or focus: what a control does, or why it is
 * off. After the same rest as a word that explains itself (B-374).
 */
export function Hint({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip label={label} withArrow openDelay={HELP_DELAY_MS}>
      <span className="fsp-ui-hint">{children}</span>
    </Tooltip>
  );
}

/**
 * A small panel a control opens in place: a "why" popover, a picker.
 *
 * `button` is the usual opener, an icon button that carries the popover's
 * open and shut state itself, so a screen reader hears what it does. `target`
 * is for anything else, wrapped in a span that carries it instead (the tour's
 * clock, whose opener is a hint around a button).
 */
export function Popover({
  button,
  target,
  children,
  width = 300,
  opened,
  onChange,
}: {
  button?: { icon: IconName; label: string; variant?: 'primary' | 'secondary' | 'quiet' };
  target?: ReactNode;
  children: ReactNode;
  width?: number;
  opened?: boolean;
  onChange?: (opened: boolean) => void;
}) {
  return (
    <MantinePopover
      width={width}
      position="bottom-start"
      withArrow
      shadow="md"
      opened={opened}
      onChange={onChange}
    >
      <MantinePopover.Target>
        {button ? (
          <IconButton icon={button.icon} label={button.label} variant={button.variant} />
        ) : (
          <span className="fsp-ui-hint">{target}</span>
        )}
      </MantinePopover.Target>
      <MantinePopover.Dropdown>{children}</MantinePopover.Dropdown>
    </MantinePopover>
  );
}

export type MenuEntry =
  | { kind: 'label'; label: string }
  | { kind: 'divider' }
  | {
      kind: 'item';
      key: string;
      label: ReactNode;
      icon?: IconName;
      /** A mark instead of an icon: a team's colors. */
      lead?: ReactNode;
      trail?: ReactNode;
      current?: boolean;
      danger?: boolean;
      /** The menu stays open after it: a setting that cycles in place. */
      keepOpen?: boolean;
      /** Shown and not pressable: a setting the moment has fixed, its trail saying to what. */
      disabled?: boolean;
      onClick: () => void;
    };

/**
 * A list of choices a control opens: the league switcher, the account menu.
 * On a phone it opens as a sheet from the bottom (B-311): a menu dropped from
 * the top bar is the reach a thumb has least of (UX-7), and an app's menus
 * on a phone come up from the bottom. `title` heads that sheet.
 */
export function Menu({
  target,
  entries,
  width = 280,
  align = 'start',
  maxHeight,
  title,
}: {
  target: ReactNode;
  entries: readonly MenuEntry[];
  width?: number;
  align?: 'start' | 'end';
  /** A long list scrolls inside the menu rather than off the screen: a season's weeks. */
  maxHeight?: number;
  /** What the phone's sheet is called: "Leagues", "Pick a week". */
  title?: string;
}) {
  const phone = useViewportWidth(isPhoneWidth);
  if (phone && isValidElement<{ onClick?: (event: PointerClick) => void }>(target)) {
    return <SheetMenu target={target} entries={entries} title={title} />;
  }
  return (
    <MantineMenu
      position={align === 'start' ? 'bottom-start' : 'bottom-end'}
      width={width}
      shadow="md"
      withArrow
      // NO EMPTY FOCUSABLE BOX FIRST IN THE MENU: Mantine's placeholder is a
      // child a menu may not have (axe aria-required-children), and without
      // it the focus trap lands on the first entry, as the menu pattern has it.
      withInitialFocusPlaceholder={false}
    >
      <MantineMenu.Target>{target}</MantineMenu.Target>
      <MantineMenu.Dropdown
        mah={maxHeight}
        className={maxHeight === undefined ? undefined : 'fsp-ui-menu-scroll'}
      >
        {entries.map((e, i) =>
          e.kind === 'label' ? (
            <MantineMenu.Label key={`label-${i}`}>{e.label}</MantineMenu.Label>
          ) : e.kind === 'divider' ? (
            <MantineMenu.Divider key={`divider-${i}`} />
          ) : (
            <MantineMenu.Item
              key={e.key}
              onClick={e.onClick}
              color={e.danger ? 'red' : undefined}
              leftSection={e.lead ?? (e.icon ? <Icon name={e.icon} /> : undefined)}
              rightSection={e.trail}
              closeMenuOnClick={!e.keepOpen}
              disabled={e.disabled}
              className={e.current ? 'fsp-ui-menu-current' : undefined}
            >
              {e.label}
            </MantineMenu.Item>
          ),
        )}
      </MantineMenu.Dropdown>
    </MantineMenu>
  );
}

/**
 * A ring drawn around a rectangle of the screen: the tour's spotlight on the
 * thing a step is about. The rectangle is measured, so it is the one place a
 * position is set in code rather than a stylesheet.
 */
export function Ring({
  rect,
  className,
}: {
  rect: { left: number; top: number; width: number; height: number };
  className: string;
}) {
  return (
    <div
      className={className}
      aria-hidden
      style={{
        left: rect.left - 4,
        top: rect.top - 4,
        width: rect.width + 8,
        height: rect.height + 8,
      }}
    />
  );
}

/** A sheet's row height, for sizing the sheet to its entries. */
const ROW = 52;

/** The phone's menu: the same entries as a list in a sheet from the bottom. */
function SheetMenu({
  target,
  entries,
  title,
}: {
  target: ReactElement<{ onClick?: (event: PointerClick) => void }>;
  entries: readonly MenuEntry[];
  title?: string;
}) {
  const [opened, setOpened] = useState(false);
  const trigger = cloneElement(target, {
    onClick: (event: PointerClick) => {
      target.props.onClick?.(event);
      setOpened(true);
    },
    'aria-haspopup': 'dialog',
    'aria-expanded': opened,
  } as Record<string, unknown>);
  // A label that only repeats the sheet's title (the account's name) is said once.
  const shown = entries.filter((e, i) => !(i === 0 && e.kind === 'label' && e.label === title));
  const count = (kind: MenuEntry['kind']) => shown.filter((e) => e.kind === kind).length;
  // A bottom sheet does not size to its content: the title bar, rows, labels
  // and rules, the body's padding, and never more than most of the screen,
  // where the list scrolls.
  const height = Math.min(
    72 + count('item') * ROW + count('label') * 32 + count('divider') * 10 + 28,
    Math.round(window.innerHeight * 0.85),
  );
  return (
    <>
      {trigger}
      <Sheet opened={opened} onClose={() => setOpened(false)} title={title} height={height}>
        <div className="fsp-ui-sheet-menu" role="menu">
          {shown.map((e, i) =>
            e.kind === 'label' ? (
              <div key={`label-${i}`} className="fsp-ui-sheet-menu-label" role="presentation">
                {e.label}
              </div>
            ) : e.kind === 'divider' ? (
              <div key={`divider-${i}`} className="fsp-ui-sheet-menu-divider" role="separator" />
            ) : (
              <button
                key={e.key}
                type="button"
                role="menuitem"
                className="fsp-ui-sheet-menu-item"
                data-danger={e.danger || undefined}
                aria-current={e.current || undefined}
                disabled={e.disabled}
                onClick={() => {
                  if (!e.keepOpen) setOpened(false);
                  e.onClick();
                }}
              >
                <span className="fsp-ui-sheet-menu-lead">
                  {e.lead ?? (e.icon ? <Icon name={e.icon} /> : null)}
                </span>
                <span className="fsp-ui-sheet-menu-text">{e.label}</span>
                {e.trail ? <span className="fsp-ui-sheet-menu-trail">{e.trail}</span> : null}
                {e.current ? <Icon name="accept" size="sm" /> : null}
              </button>
            ),
          )}
        </div>
      </Sheet>
    </>
  );
}
